import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { useCurrencies } from '../hooks/useCurrencies';
import AuditPopover from '../components/AuditPopover';
import { TabIcon, TAB_ICONS } from '../utils/tabIcons';
import QuickListManager from '../components/QuickListManager';
import DataTable from '../components/DataTable';
import AttachmentManager from '../components/AttachmentManager';
import CustomFieldsSection from '../components/CustomFieldsSection';
import VendorScorecard from '../components/VendorScorecard';
import { fmtAmount, fmtRate } from '../utils/numberFormat';
import { Button, EmptyState, Modal, FormField, Pager, ListPageHeader, ListPageStats, FilterBar } from '../components/ui';
import { CardHero, CardStatusBadge, CardTabs, CardSplitButton } from '../components/card';

const IMG_BASE = window.location.hostname === 'localhost' ? 'http://localhost:5001' : '';

const TYPES = [
  { value: 'customer',         label: 'Müşteri' },
  { value: 'supplier',         label: 'Satıcı / Tedarikçi' },
  { value: 'both',             label: 'Müşteri + Tedarikçi' },
  { value: 'retail_customer',  label: 'Perakende Müşteri' },
  { value: 'dealer',           label: 'Bayii' },
  { value: 'personnel',        label: 'Personel' },
  { value: 'ecommerce',        label: 'ETicaret' },
  { value: 'other',            label: 'Diğer' },
];
const ID_TYPES = [
  { value: 'corporate', label: 'Tuzel Kisi (Sirket)' },
  { value: 'individual', label: 'Gercek Kisi (Sahis)' },
];

const emptyAccount = {
  code: '', title: '', type: 'customer', identity_type: 'corporate',
  tax_office: '', tax_number: '', id_number: '',
  phone: '', phone2: '', mobile: '', fax: '', email: '', website: '',
  address: '', city: '', district: '', country: 'Turkiye', postal_code: '',
  group_id: '', category_id: '', region_id: '', special_code_id: '', sales_rep_id: '', sector: '',
  branch_id: '',
  credit_limit: 0, credit_limit_action: 'warn', payment_term_days: 0, discount_rate: 0, currency_code: 'TRY',
  notes: '', is_active: true
};

function AccountsPage({ autoOpenNew = false, onSaved = null, onNavigate = null }) {
  const { user } = useAuth();
  const { t } = useTranslation();
  const { currencies } = useCurrencies();
  const [accounts, setAccounts] = useState([]);
  const [stats, setStats] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({...emptyAccount});
  const [isEditing, setIsEditing] = useState(false);
  const [isNew, setIsNew] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [lookups, setLookups] = useState({ groups: [], categories: [], regions: [], reps: [] });
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [showQuickAdd, setShowQuickAdd] = useState(null);
  const [quickForm, setQuickForm] = useState({ code: '', name: '' });
  const [contacts, setContacts] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [addrForm, setAddrForm] = useState({ label: '', contact_person: '', phone: '', address: '', district: '', city: '', country: 'Türkiye', postal_code: '', is_default: false, notes: '' });
  const [editingAddrId, setEditingAddrId] = useState(null);
  const [showAddrForm, setShowAddrForm] = useState(false);
  // Adres form'una özel ülke/şehir/ilçe lookup chain (cari ana formdan ayrı)
  const [addrCities, setAddrCities] = useState([]);
  const [addrDistricts, setAddrDistricts] = useState([]);
  const [banks, setBanks] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [waybills, setWaybills] = useState([]);
  const [waybillsLoading, setWaybillsLoading] = useState(false);
  // Cari hareketinde detay butonu — bağlı belgeyi kendi modülünde tam aç (deep-link)
  const openLinkedDoc = (tx) => {
    if (!onNavigate) return;
    const src = tx.source_type;
    const dt  = tx.doc_type || '';
    // Cari açılış / borç / alacak / virman fişleri self-link hareketlerdir;
    // source_id boş olur, tx.id deep-link için kullanılır.
    const SELF_LINK_DTS = ['cari_opening', 'cari_debit', 'cari_credit', 'cari_virman_in', 'cari_virman_out'];
    const linkId = tx.source_id || (SELF_LINK_DTS.includes(dt) ? tx.id : null);
    if (!linkId) return;
    let targetPage = null;
    if (src === 'sales_invoice') {
      targetPage = (dt === 'sales_return' || dt === 'retail_sale_return') ? 'sales-returns' : 'sales';
    } else if (src === 'purchase_invoice') {
      targetPage = dt === 'purchase_return' ? 'purchases-returns' : 'purchases';
    } else if (src === 'sales_waybill') {
      targetPage = dt === 'sales_waybill_return' ? 'waybills-sales' : 'waybills-sales';
    } else if (src === 'purchase_waybill') {
      targetPage = 'waybills-purchase';
    } else if (src === 'cari_receipt') {
      targetPage = 'cari-tahsilat';
    } else if (src === 'cari_payment') {
      targetPage = 'cari-tediye';
    } else if (src === 'expense_voucher') {
      targetPage = 'expense-vouchers';
    } else if (src === 'checks_notes') {
      // Çek/senet — yön + tip bağlamına göre doğru sayfayı seç
      if (dt === 'check_in')       targetPage = 'cek-portfoy';
      else if (dt === 'check_out') targetPage = 'cek-verilen';
      else if (dt === 'note_in')   targetPage = 'senet-portfoy';
      else if (dt === 'note_out')  targetPage = 'senet-verilen';
      else                         targetPage = 'cek-portfoy'; // default fallback
    } else if (src === 'cari_voucher' || src === 'cari_opening' || dt === 'cari_opening') {
      targetPage = 'cari-acilis';
    } else if (dt === 'cari_debit') {
      targetPage = 'cari-borc';
    } else if (dt === 'cari_credit') {
      targetPage = 'cari-alacak';
    } else if (dt === 'cari_virman_in' || dt === 'cari_virman_out') {
      targetPage = 'cari-virman';
    }
    if (targetPage) {
      handleModalClose();  // mevcut cari modalini kapat, yeni belgeye git
      onNavigate(targetPage, linkId);
    } else {
      alert('Bu kaynak tipi için yönlendirme tanımlı değil: ' + (src || dt || '?'));
    }
  };
  const [showContactForm, setShowContactForm] = useState(false);
  const [showBankForm, setShowBankForm] = useState(false);
  const [contactForm, setContactForm] = useState({ full_name: '', title: '', department: '', phone: '', mobile: '', email: '', is_primary: false });
  const [bankForm, setBankForm] = useState({ bank_name: '', branch_name: '', account_number: '', iban: '', currency_code: 'TRY', is_default: false });
  const [showModal, setShowModal] = useState(false);
  const [imgUploading, setImgUploading] = useState(false);
  const imgInputRef = useRef(null);
  const [showFilterBar, setShowFilterBar] = useState(false);
  const [filters, setFilters] = useState({ type: '', group_id: '', category_id: '', region_id: '', is_active: '', balance_state: '' });

  const fetchAccounts = useCallback(async () => {
    setLoading(true);
    try {
      const params = { search, page, limit: 50 };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await api.get('/accounts', { params });
      if (res.data.success) { setAccounts(res.data.data.items); setTotal(res.data.data.total); }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [search, page, filters]);

  const fetchLookups = async () => {
    try {
      const res = await api.get('/accounts/lookups/all');
      const repsRes = await api.get('/personnel/sales-reps', { params: { module: 'accounts' } });
      const branchRes = await api.get('/branches', { params: { is_active: 'yes' } }).catch(() => ({ data: { success: false } }));
      if (res.data.success) {
        const data = res.data.data;
        if (repsRes.data.success) data.reps = repsRes.data.data;
        data.branches = branchRes.data?.success ? (branchRes.data.data.items || []) : [];
        setLookups(data);
      }
    } catch (err) { console.error(err); }
  };

  useEffect(() => { fetchAccounts(); }, [fetchAccounts]);

  const fetchStats = useCallback(async () => {
    try {
      const r = await api.get('/accounts/stats');
      if (r.data.success) setStats(r.data.data);
    } catch {}
  }, []);
  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { fetchLookups(); }, []);
  // Yer tanımları (Ülke / Şehir / İlçe)
  useEffect(() => {
    api.get('/locations/countries').then(r => { if (r.data.success) setCountries(r.data.data || []); }).catch(() => {});
  }, []);
  useEffect(() => {
    if (!form.country) { setCities([]); return; }
    const co = countries.find(c => c.name === form.country);
    if (!co) { setCities([]); return; }
    api.get('/locations/cities', { params: { country_id: co.id } })
      .then(r => { if (r.data.success) setCities(r.data.data || []); }).catch(() => {});
  }, [form.country, countries]);
  useEffect(() => {
    if (!form.city) { setDistricts([]); return; }
    const ci = cities.find(c => c.name === form.city);
    if (!ci) { setDistricts([]); return; }
    api.get('/locations/districts', { params: { city_id: ci.id } })
      .then(r => { if (r.data.success) setDistricts(r.data.data || []); }).catch(() => {});
  }, [form.city, cities]);

  // Adres formu için ayrı ülke→şehir→ilçe chain'i
  useEffect(() => {
    if (!addrForm.country) { setAddrCities([]); return; }
    const co = countries.find(c => c.name === addrForm.country);
    if (!co) { setAddrCities([]); return; }
    api.get('/locations/cities', { params: { country_id: co.id } })
      .then(r => { if (r.data.success) setAddrCities(r.data.data || []); }).catch(() => {});
  }, [addrForm.country, countries]);
  useEffect(() => {
    if (!addrForm.city) { setAddrDistricts([]); return; }
    const ci = addrCities.find(c => c.name === addrForm.city);
    if (!ci) { setAddrDistricts([]); return; }
    api.get('/locations/districts', { params: { city_id: ci.id } })
      .then(r => { if (r.data.success) setAddrDistricts(r.data.data || []); }).catch(() => {});
  }, [addrForm.city, addrCities]);
  // Overlay modunda açılır açılmaz "Yeni" formunu aç
  useEffect(() => {
    if (autoOpenNew) {
      setSelected(null); setForm({...emptyAccount}); setIsEditing(true); setIsNew(true);
      setActiveTab('general'); setMsg(null); setContacts([]); setBanks([]); setTransactions([]); setWaybills([]);
      setShowModal(true);
    }
  }, [autoOpenNew]);

  const loadSubData = async (id) => {
    try {
      const [c, b, t, ad] = await Promise.all([
        api.get('/accounts/' + id + '/contacts'),
        api.get('/accounts/' + id + '/banks'),
        api.get('/accounts/' + id + '/transactions'),
        api.get('/accounts/' + id + '/addresses').catch(() => ({ data: { success: false } })),
      ]);
      if (c.data.success) setContacts(c.data.data);
      if (b.data.success) setBanks(b.data.data);
      if (t.data.success) setTransactions(t.data.data);
      if (ad.data.success) setAddresses(ad.data.data || []);
    } catch (err) { console.error(err); }
  };

  const reloadAddresses = async () => {
    if (!selected?.id) return;
    try {
      const r = await api.get('/accounts/' + selected.id + '/addresses');
      if (r.data.success) setAddresses(r.data.data || []);
    } catch (e) { console.error(e); }
  };

  const saveAddress = async () => {
    if (!selected?.id || !addrForm.label.trim() || !addrForm.address.trim()) {
      setMsg({ type: 'error', text: 'Etiket ve adres zorunlu' });
      return;
    }
    try {
      if (editingAddrId) {
        await api.put('/accounts/' + selected.id + '/addresses/' + editingAddrId, addrForm);
      } else {
        await api.post('/accounts/' + selected.id + '/addresses', addrForm);
      }
      setAddrForm({ label: '', contact_person: '', phone: '', address: '', district: '', city: '', country: 'Türkiye', postal_code: '', is_default: false, notes: '' });
      setEditingAddrId(null);
      await reloadAddresses();
      setMsg({ type: 'success', text: 'Adres kaydedildi' });
    } catch (e) { setMsg({ type: 'error', text: e.response?.data?.message || 'Hata' }); }
  };

  const editAddress = (a) => {
    setAddrForm({
      label: a.label || '', contact_person: a.contact_person || '', phone: a.phone || '',
      address: a.address || '', district: a.district || '', city: a.city || '',
      country: a.country || 'Türkiye', postal_code: a.postal_code || '',
      is_default: !!a.is_default, notes: a.notes || '',
    });
    setEditingAddrId(a.id);
  };

  const deleteAddress = async (id) => {
    if (!window.confirm('Adres silinsin mi?')) return;
    try {
      await api.delete('/accounts/' + selected.id + '/addresses/' + id);
      await reloadAddresses();
    } catch (e) { setMsg({ type: 'error', text: 'Silinemedi' }); }
  };

  const makeAddressDefault = async (id) => {
    try {
      await api.post('/accounts/' + selected.id + '/addresses/' + id + '/default');
      await reloadAddresses();
    } catch (e) { setMsg({ type: 'error', text: 'İşlem başarısız' }); }
  };

  // Waybills sekmesi tıklanınca lazy yükle
  useEffect(() => {
    if (activeTab !== 'waybills' || !selected?.id) return;
    setWaybillsLoading(true);
    api.get('/accounts/' + selected.id + '/waybills')
      .then(r => { if (r.data.success) setWaybills(r.data.data || []); })
      .catch(() => setWaybills([]))
      .finally(() => setWaybillsLoading(false));
  }, [activeTab, selected?.id]);

  const handleSelect = async (acc) => {
    try {
      const res = await api.get('/accounts/' + acc.id);
      if (res.data.success) {
        setSelected(res.data.data); setForm(res.data.data);
        setIsEditing(false); setIsNew(false); setActiveTab('general'); setMsg(null);
        setShowModal(true);
        loadSubData(acc.id);
      }
    } catch (err) { console.error(err); }
  };

  const handleNew = () => {
    setSelected(null); setForm({...emptyAccount}); setIsEditing(true); setIsNew(true);
    setActiveTab('general'); setMsg(null); setContacts([]); setBanks([]); setTransactions([]); setWaybills([]);
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false); setSelected(null); setForm({...emptyAccount});
    setIsEditing(false); setIsNew(false); setMsg(null);
  };

  const handleEdit = () => { setIsEditing(true); setMsg(null); };
  const handleCancel = () => {
    if (isNew) { handleModalClose(); }
    else { setForm(selected); setIsEditing(false); setMsg(null); }
  };

  // mode: 'close' (default) | 'new' (kaydet ve yeni aç) | 'copy' (kaydet ve kopyala)
  const handleSave = async (mode = 'close') => {
    if (mode && typeof mode === 'object') mode = 'close';
    if (!form.title) { setMsg({ type: 'error', text: 'Cari unvanı zorunludur (kod boş bırakılabilir, otomatik üretilir)' }); return; }
    setSaving(true); setMsg(null);
    try {
      if (isNew) {
        const res = await api.post('/accounts', form);
        if (res.data.success) {
          setMsg({ type: 'success', text: 'Cari kart olusturuldu' }); setIsNew(false); setIsEditing(false);
          fetchAccounts();
          const detail = await api.get('/accounts/' + res.data.data.id);
          if (detail.data.success) { setSelected(detail.data.data); setForm(detail.data.data); loadSubData(res.data.data.id); }
          if (onSaved) onSaved(detail.data?.data || res.data.data);
        }
      } else {
        const res = await api.put('/accounts/' + selected.id, form);
        if (res.data.success) {
          setMsg({ type: 'success', text: 'Cari kart guncellendi' }); setIsEditing(false);
          fetchAccounts();
          const detail = await api.get('/accounts/' + selected.id);
          if (detail.data.success) { setSelected(detail.data.data); setForm(detail.data.data); }
        }
      }
      // Post-save mode handling
      if (mode === 'close') {
        setTimeout(() => handleModalClose(), 250);
      } else if (mode === 'new') {
        setTimeout(() => {
          setSelected(null); setForm({ ...emptyAccount });
          setIsEditing(true); setIsNew(true); setActiveTab('general'); setMsg(null);
        }, 250);
      } else if (mode === 'copy') {
        setTimeout(() => {
          const copy = { ...form, id: undefined, code: '', tax_number: '', identity_number: '' };
          setSelected(null); setForm(copy);
          setIsEditing(true); setIsNew(true); setActiveTab('general'); setMsg(null);
        }, 250);
      }
    } catch (err) { setMsg({ type: 'error', text: err.response?.data?.message || 'Hata olustu' }); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!selected || !window.confirm('Bu cari karti silmek istediginize emin misiniz?')) return;
    try {
      await api.delete('/accounts/' + selected.id);
      handleModalClose(); fetchAccounts();
    } catch(e) {
      if (e.response?.status === 409 && e.response.data?.hasMovements) {
        setMsg({ type: 'error', text: e.response.data.message || 'Önce bu kayda bağlı hareketleri silin' });
      } else { setMsg({ type: 'error', text: e.response?.data?.message || 'Silme hatası' }); }
    }
  };

  const handleQuickAdd = async () => {
    if (!quickForm.code || !quickForm.name) return;
    try {
      const endpoint = showQuickAdd === 'group' ? '/accounts/groups' : showQuickAdd === 'category' ? '/accounts/categories' : '/accounts/regions';
      await api.post(endpoint, quickForm);
      fetchLookups(); setShowQuickAdd(null); setQuickForm({ code: '', name: '' });
    } catch (err) { alert('Ekleme hatasi: ' + (err.response?.data?.message || err.message)); }
  };

  const handleAddContact = async () => {
    if (!contactForm.full_name) return;
    try {
      await api.post('/accounts/' + selected.id + '/contacts', contactForm);
      loadSubData(selected.id); setShowContactForm(false);
      setContactForm({ full_name: '', title: '', department: '', phone: '', mobile: '', email: '', is_primary: false });
    } catch (err) { alert('Hata: ' + err.message); }
  };

  const handleDeleteContact = async (id) => {
    if (!window.confirm('Silmek istediginize emin misiniz?')) return;
    try { await api.delete('/accounts/contacts/' + id); loadSubData(selected.id); } catch (err) { alert('Hata'); }
  };

  const handleAddBank = async () => {
    if (!bankForm.bank_name) return;
    try {
      await api.post('/accounts/' + selected.id + '/banks', bankForm);
      loadSubData(selected.id); setShowBankForm(false);
      setBankForm({ bank_name: '', branch_name: '', account_number: '', iban: '', currency_code: 'TRY', is_default: false });
    } catch (err) { alert('Hata: ' + err.message); }
  };

  const handleDeleteBank = async (id) => {
    if (!window.confirm('Silmek istediginize emin misiniz?')) return;
    try { await api.delete('/accounts/banks/' + id); loadSubData(selected.id); } catch (err) { alert('Hata'); }
  };

  const f = (key, val) => setForm(prev => ({...prev, [key]: val}));

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !selected?.id) return;
    setImgUploading(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const res = await api.post('/accounts/' + selected.id + '/image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (res.data.success) { const updated = { ...selected, image_url: res.data.image_url }; setSelected(updated); setForm(updated); fetchAccounts(); }
    } catch (err) { setMsg({ type: 'error', text: 'Resim yüklenemedi' }); }
    finally { setImgUploading(false); if (imgInputRef.current) imgInputRef.current.value = ''; }
  };

  const handleImageDelete = async () => {
    if (!selected?.id || !window.confirm('Resmi silmek istiyor musunuz?')) return;
    try {
      await api.delete('/accounts/' + selected.id + '/image');
      const updated = { ...selected, image_url: null }; setSelected(updated); setForm(updated); fetchAccounts();
    } catch (err) { setMsg({ type: 'error', text: 'Resim silinemedi' }); }
  };

  const tabs = [
    { key: 'general',      label: t('tabs.accountInfo'),  icon: 'id' },
    { key: 'contact',      label: t('tabs.contact'),      icon: 'phone' },
    { key: 'financial',    label: t('tabs.financial'),    icon: 'briefcase' },
    { key: 'persons',      label: t('tabs.persons'),      icon: 'user' },
    { key: 'addresses',    label: t('tabs.addresses'),    icon: 'pin' },
    { key: 'banks',        label: t('tabs.bankAccounts'), icon: 'bank' },
    { key: 'transactions', label: t('tabs.transactions'), icon: 'activity' },
    { key: 'waybills',     label: t('tabs.waybills'),     icon: 'package' },
    ...((selected?.type === 'supplier' || selected?.type === 'both') ? [{ key: 'scorecard', label: t('tabs.scorecard'), icon: 'sparkles' }] : []),
    { key: 'image',        label: t('tabs.image'),        icon: 'image' },
    { key: 'custom',       label: t('tabs.custom'),        icon: 'sparkles' },
    { key: 'documents',    label: t('tabs.documents'),     icon: 'paperclip' },
    { key: 'notes',        label: t('tabs.notes'),        icon: 'notes' },
  ];

  const typeLabel = (t) => TYPES.find(x => x.value === t)?.label || t || '—';
  const TYPE_COLORS = {
    customer:        { bg: 'rgba(16,185,129,0.1)',  clr: 'var(--success)' },
    supplier:        { bg: 'rgba(59,130,246,0.1)',  clr: 'var(--info)' },
    both:            { bg: 'rgba(139,92,246,0.1)',  clr: 'var(--accent-primary)' },
    retail_customer: { bg: 'rgba(245,158,11,0.12)', clr: '#f59e0b' },
    dealer:          { bg: 'rgba(168,85,247,0.12)', clr: '#a855f7' },
    personnel:       { bg: 'rgba(2,132,199,0.12)',  clr: '#0284c7' },
    ecommerce:       { bg: 'rgba(236,72,153,0.12)', clr: '#ec4899' },
    other:           { bg: 'rgba(107,114,128,0.12)',clr: 'var(--text-secondary)' },
  };
  const typeBg  = (t) => TYPE_COLORS[t]?.bg  || 'rgba(107,114,128,0.12)';
  const typeClr = (t) => TYPE_COLORS[t]?.clr || 'var(--text-secondary)';

  return (
    <div className="stocks-page">
      <ListPageHeader
        title="Cari Hesaplar"
        count={total}
        unit="kayit"
        search={search}
        onSearch={v => { setSearch(v); setPage(1); }}
        searchPlaceholder="Kod, unvan, telefon ara..."
        onRefresh={() => { fetchAccounts(); fetchStats(); }}
        refreshing={loading}
        actions={<Button size="sm" onClick={handleNew}>+ Yeni Cari</Button>}
      />

      <ListPageStats
        loading={stats == null}
        stats={[
          {
            key: 'total',
            label: 'Toplam Cari',
            value: stats?.total,
            delta: stats?.new_30d > 0 ? stats.new_30d : null,
            deltaTooltip: 'Son 30 günde eklenen',
            icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
          },
          {
            key: 'receivable',
            label: 'Alacaklı (Bizden Borçlu)',
            value: stats?.total_receivable,
            color: 'success',
            sublabel: `${stats?.debtor_count || 0} cari`,
            icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 17 13.5 8.5 8.5 13.5 2 7"/><polyline points="16 17 22 17 22 11"/></svg>,
          },
          {
            key: 'payable',
            label: 'Borçlu (Bize Alacaklı)',
            value: stats?.total_payable,
            color: 'warning',
            sublabel: `${stats?.creditor_count || 0} cari`,
            icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></svg>,
          },
          {
            key: 'net',
            label: 'Net Bakiye',
            value: (stats?.total_receivable || 0) - (stats?.total_payable || 0),
            color: 'primary',
            sublabel: 'alacak - borç',
            icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>,
          },
        ]}
      />

      <FilterBar
        show={showFilterBar}
        onToggle={() => setShowFilterBar(s => !s)}
        values={filters}
        onChange={setFilters}
        fields={[
          { key: 'type', type: 'select', label: 'Tip',
            options: [{ value: '', label: 'Tümü' }, { value: 'customer', label: 'Müşteri' }, { value: 'supplier', label: 'Tedarikçi' }, { value: 'both', label: 'Müşteri/Tedarikçi' }] },
          { key: 'group_id', type: 'select', label: 'Grup', flex: '1 1 200px',
            options: [{ value: '', label: 'Tümü' }, ...((lookups.groups || []).map(g => ({ value: g.id, label: g.name })))] },
          { key: 'category_id', type: 'select', label: 'Kategori', flex: '1 1 200px',
            options: [{ value: '', label: 'Tümü' }, ...((lookups.categories || []).map(c => ({ value: c.id, label: c.name })))] },
          { key: 'is_active', type: 'select', label: 'Durum',
            options: [{ value: '', label: 'Tümü' }, { value: 'yes', label: 'Aktif' }, { value: 'no', label: 'Pasif' }] },
          { key: 'balance_state', type: 'select', label: 'Bakiye',
            options: [{ value: '', label: 'Tümü' }, { value: 'debtor', label: 'Borçlu (+)' }, { value: 'creditor', label: 'Alacaklı (−)' }, { value: 'zero', label: 'Sıfır' }] },
        ]}
      />

      {/* TABLO */}
      <div className="stocks-table-wrap">
        <DataTable
          pageKey="accounts-list"
          tableClassName="stocks-table"
          rowClassName="stocks-table-row"
          loading={loading}
          emptyText="Cari bulunamadı"
          data={accounts}
          rowKey="id"
          onRowClick={a => handleSelect(a)}
          bulkActions={[
            {
              key: 'activate', label: 'Aktif Yap', variant: 'ghost',
              onClick: async (ids, clear) => {
                try { const r = await api.post('/accounts/bulk', { action: 'set_active', ids });
                  if (r.data.success) { clear(); fetchAccounts(); fetchStats(); }
                } catch (e) { alert(e.response?.data?.message || e.message); }
              },
            },
            {
              key: 'deactivate', label: 'Pasif Yap', variant: 'ghost',
              confirm: 'Seçili cariler pasif yapılacak. Emin misiniz?',
              onClick: async (ids, clear) => {
                try { const r = await api.post('/accounts/bulk', { action: 'set_inactive', ids });
                  if (r.data.success) { clear(); fetchAccounts(); fetchStats(); }
                } catch (e) { alert(e.response?.data?.message || e.message); }
              },
            },
            {
              key: 'set_group', label: 'Grup Ata', variant: 'primary',
              prompt: {
                type: 'select', title: 'Toplu Cari Grubu Atama',
                description: 'Seçili carilere atanacak grubu seçin:',
                label: 'Cari Grubu',
                options: (lookups.groups || []).map(g => ({ value: g.id, label: g.name })),
                allowEmpty: true, emptyLabel: '— Grubu kaldır —',
              },
              onClick: async (ids, clear, value) => {
                try { const r = await api.post('/accounts/bulk', { action: 'set_group', ids, value });
                  if (r.data.success) { clear(); fetchAccounts(); }
                } catch (e) { alert(e.response?.data?.message || e.message); }
              },
            },
            {
              key: 'set_category', label: 'Kategori Ata', variant: 'primary',
              prompt: {
                type: 'select', title: 'Toplu Cari Kategori Atama',
                description: 'Seçili carilere atanacak kategoriyi seçin:',
                label: 'Kategori',
                options: (lookups.categories || []).map(c => ({ value: c.id, label: c.name })),
                allowEmpty: true, emptyLabel: '— Kategoriyi kaldır —',
              },
              onClick: async (ids, clear, value) => {
                try { const r = await api.post('/accounts/bulk', { action: 'set_category', ids, value });
                  if (r.data.success) { clear(); fetchAccounts(); }
                } catch (e) { alert(e.response?.data?.message || e.message); }
              },
            },
            {
              key: 'set_region', label: 'Bölge Ata', variant: 'primary',
              prompt: {
                type: 'select', title: 'Toplu Bölge Atama',
                description: 'Seçili carilere atanacak bölgeyi seçin:',
                label: 'Bölge',
                options: (lookups.regions || []).map(r => ({ value: r.id, label: r.name })),
                allowEmpty: true, emptyLabel: '— Bölgeyi kaldır —',
              },
              onClick: async (ids, clear, value) => {
                try { const r = await api.post('/accounts/bulk', { action: 'set_region', ids, value });
                  if (r.data.success) { clear(); fetchAccounts(); }
                } catch (e) { alert(e.response?.data?.message || e.message); }
              },
            },
            {
              key: 'delete', label: 'Sil', variant: 'danger',
              confirm: 'Seçili cariler SİLİNECEK. Hareketi/bakiyesi olanlar silinemez. Devam edilsin mi?',
              onClick: async (ids, clear) => {
                try { const r = await api.post('/accounts/bulk', { action: 'delete', ids });
                  if (r.data.success) { clear(); fetchAccounts(); fetchStats(); }
                } catch (e) { alert(e.response?.data?.message || e.message); }
              },
            },
          ]}
          columns={[
            { id: 'code',  header: 'Cari Kodu', accessor: 'code', sortable: true,
              cell: a => <span className="stock-code">{a.code}</span>, width: 130 },
            { id: 'title', header: 'Unvan',     accessor: 'title', sortable: true,
              cell: a => <span className="stock-name-cell">{a.title}</span> },
            { id: 'type',  header: 'Tip',       accessor: 'type', sortable: true,
              cell: a => <span className="stock-badge active" style={{background: typeBg(a.type), color: typeClr(a.type)}}>{typeLabel(a.type)}</span>,
              width: 110 },
            { id: 'phone', header: 'Telefon',   accessor: a => a.phone || a.mobile || '', sortable: true,
              cell: a => <span className="stock-secondary">{a.phone || a.mobile || '-'}</span>, width: 130 },
            { id: 'tax',   header: 'VD / TC No',
              accessor: a => a.identity_type === 'individual'
                ? (a.id_number || '')
                : `${a.tax_office || ''} ${a.tax_number || ''}`,
              cell: a => (
                <span className="stock-secondary" style={{fontSize:'0.78rem'}}>
                  {a.identity_type === 'individual'
                    ? (a.id_number || '-')
                    : (a.tax_office || a.tax_number ? `${a.tax_office || ''}${a.tax_office && a.tax_number ? ' / ' : ''}${a.tax_number || ''}` : '-')
                  }
                </span>
              ), width: 180 },
            { id: 'city',  header: 'Şehir', accessor: 'city', sortable: true,
              cell: a => <span className="stock-secondary">{a.city || '-'}</span>, width: 120 },
            { id: 'balance', header: 'Bakiye', accessor: a => parseFloat(a.balance) || 0, sortable: true,
              headerStyle: { textAlign: 'right' }, cellStyle: { textAlign: 'right' }, width: 200,
              cell: a => (
                <div style={{display:'inline-flex', alignItems:'center', gap:8, justifyContent:'flex-end'}}>
                  <span className="stock-price-cell" style={{color: a.balance > 0 ? 'var(--danger)' : a.balance < 0 ? 'var(--success)' : 'var(--text-tertiary)'}}>
                    {fmtAmount(Math.abs(a.balance || 0))} {a.currency_code}
                  </span>
                  {(() => {
                    const b = parseFloat(a.balance) || 0;
                    if (b > 0)  return <span style={{padding:'2px 8px',borderRadius:4,fontSize:'0.7rem',fontWeight:600,background:'rgba(239,68,68,0.15)',color:'var(--danger)'}}>Borç</span>;
                    if (b < 0)  return <span style={{padding:'2px 8px',borderRadius:4,fontSize:'0.7rem',fontWeight:600,background:'rgba(34,197,94,0.15)',color:'var(--success)'}}>Alacak</span>;
                    return <span style={{padding:'2px 8px',borderRadius:4,fontSize:'0.7rem',fontWeight:600,background:'rgba(100,116,139,0.15)',color:'var(--text-tertiary)'}}>Kapalı</span>;
                  })()}
                </div>
              ) },
            { id: 'active', header: 'Durum', accessor: a => a.is_active ? 'Aktif' : 'Pasif', sortable: true,
              headerStyle: { textAlign: 'center' }, cellStyle: { textAlign: 'center' }, width: 90,
              cell: a => <span className={'stock-badge ' + (a.is_active ? 'active' : 'passive')}>{a.is_active ? 'Aktif' : 'Pasif'}</span> },
            { id: 'audit', header: '', sortable: false, hideable: false, draggable: false, filterable: false, resizable: false, width: 32,
              headerStyle: { textAlign: 'center' }, cellStyle: { textAlign: 'center' },
              cell: a => <span onClick={e => e.stopPropagation()}><AuditPopover endpoint={`/accounts/${a.id}/audit`}/></span> },
          ]}
        />
      </div>

      <Pager page={page} totalPages={Math.ceil(total/50)} onChange={setPage} />

      {/* DETAY MODAL — paylaşılan UI bileşenleriyle */}
      {showModal && (() => {
        const inEdit = isEditing;
        const inView = !isEditing && !isNew;
        const modalTitle = isNew ? 'Yeni Cari Hesap' : 'Cari Kartı';

        const balanceNum = parseFloat(form.balance) || 0;
        const typeLbl = TYPES.find(t => t.value === form.type)?.label || 'Cari';
        const idTypeLbl = ID_TYPES.find(t => t.value === form.identity_type)?.label;

        const gridStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 };

        return (
          <Modal
            icon={null}
            title={modalTitle}
            size="xl"
            onClose={handleModalClose}
            footer={
              <>
                <div className="ui-modal-footer-left">
                  {inView && (
                    <Button variant="danger" onClick={handleDelete}>🗑 Sil</Button>
                  )}
                </div>
                <div className="ui-modal-footer-right" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  {inEdit && (
                    <Button variant="ghost" onClick={handleCancel}>
                      {isNew ? 'Vazgeç' : 'İptal'}
                    </Button>
                  )}
                  {inEdit && (
                    <CardSplitButton
                      primaryLabel="💾 Kaydet"
                      primaryAction={() => handleSave('close')}
                      loading={saving}
                      options={[
                        { label: 'Kaydet ve Yeni Aç', icon: '✚', onClick: () => handleSave('new') },
                        { label: 'Kaydet ve Kopyala', icon: '⎘', onClick: () => handleSave('copy') },
                        { label: 'Kaydet ve Kapat',   icon: '✓', onClick: () => handleSave('close') },
                      ]}
                    />
                  )}
                  {inView && (
                    <Button onClick={handleEdit}>✎ Düzenle</Button>
                  )}
                </div>
              </>
            }
          >
            {!isNew && (
              <CardHero
                icon="👤"
                code={form.code}
                title={form.title}
                subtitle={[typeLbl, idTypeLbl, form.tax_number && ('VKN: ' + form.tax_number)].filter(Boolean).join(' · ')}
                statusBadge={<CardStatusBadge preset={form.is_active === false ? 'inactive' : 'active'} />}
                kpis={[
                  balanceNum !== 0 && {
                    label: balanceNum > 0 ? 'Cari Bakiye (Borç)' : 'Cari Bakiye (Alacak)',
                    value: fmtAmount(Math.abs(balanceNum)),
                    color: balanceNum > 0 ? 'danger' : 'success',
                    sublabel: form.currency_code || 'TRY',
                  },
                  form.credit_limit > 0 && {
                    label: 'Kredi Limiti', value: fmtAmount(form.credit_limit), color: 'neutral',
                    sublabel: form.currency_code || 'TRY',
                  },
                  form.payment_term_days != null && {
                    label: 'Vade', value: form.payment_term_days + ' gün', color: 'info',
                  },
                  form.discount_rate > 0 && {
                    label: 'İskonto', value: '%' + fmtRate(form.discount_rate), color: 'warning',
                  },
                ].filter(Boolean)}
              />
            )}

            {msg && (
              <div style={{
                marginBottom: 12, padding: '9px 12px', borderRadius: 8, fontSize: '0.84rem',
                background: msg.type === 'error' ? 'var(--danger-bg)' : 'rgba(16,185,129,0.1)',
                color: msg.type === 'error' ? 'var(--danger)' : '#0d9488',
                border: '1px solid ' + (msg.type === 'error' ? 'rgba(239,68,68,0.25)' : 'rgba(16,185,129,0.25)'),
              }}>{msg.text}</div>
            )}

            <CardTabs
              items={tabs.map(t => ({ key: t.key, label: t.label, icon: <TabIcon name={t.icon} /> }))}
              active={activeTab}
              onChange={setActiveTab}
            />

            {activeTab === 'general' && (
              <div style={gridStyle}>
                <FormField label="Cari Kodu" required>
                  <input value={form.code||''} onChange={e=>f('code',e.target.value)} disabled={!isEditing}/>
                </FormField>
                <FormField label="Unvan / Ad" required>
                  <input value={form.title||''} onChange={e=>f('title',e.target.value)} disabled={!isEditing}/>
                </FormField>
                <FormField label="Tip">
                  <select value={form.type||'customer'} onChange={e=>f('type',e.target.value)} disabled={!isEditing}>
                    {TYPES.map(t=><option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </FormField>
                <FormField label="Kişi Tipi">
                  <select value={form.identity_type||'corporate'} onChange={e=>f('identity_type',e.target.value)} disabled={!isEditing}>
                    {ID_TYPES.map(t=><option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </FormField>
                <FormField label="Vergi Dairesi">
                  <input value={form.tax_office||''} onChange={e=>f('tax_office',e.target.value)} disabled={!isEditing}/>
                </FormField>
                <FormField label="Vergi No">
                  <input value={form.tax_number||''} onChange={e=>f('tax_number',e.target.value)} disabled={!isEditing}/>
                </FormField>
                <FormField label="TC Kimlik No">
                  <input value={form.id_number||''} onChange={e=>f('id_number',e.target.value)} disabled={!isEditing}/>
                </FormField>
                <FormField label="Sektör">
                  <input value={form.sector||''} onChange={e=>f('sector',e.target.value)} disabled={!isEditing}/>
                </FormField>
                <FormField label={<>Grup {isEditing && <button type="button" className="quick-add-btn" onClick={()=>setShowQuickAdd('group-mgr')}>+</button>}</>}>
                  <select value={form.group_id||''} onChange={e=>f('group_id',e.target.value||null)} disabled={!isEditing}>
                    <option value="">Seciniz</option>
                    {lookups.groups.map(g=><option key={g.id} value={g.id}>{g.name}</option>)}
                  </select>
                </FormField>
                <FormField label={<>Kategori {isEditing && <button type="button" className="quick-add-btn" onClick={()=>setShowQuickAdd('category-mgr')}>+</button>}</>}>
                  <select value={form.category_id||''} onChange={e=>f('category_id',e.target.value||null)} disabled={!isEditing}>
                    <option value="">Seciniz</option>
                    {lookups.categories.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </FormField>
                <FormField label={<>Bölge {isEditing && <button type="button" className="quick-add-btn" onClick={()=>setShowQuickAdd('region-mgr')}>+</button>}</>}>
                  <select value={form.region_id||''} onChange={e=>f('region_id',e.target.value||null)} disabled={!isEditing}>
                    <option value="">Seciniz</option>
                    {lookups.regions.map(r=><option key={r.id} value={r.id}>{r.name}</option>)}
                  </select>
                </FormField>
                <FormField label={<>Özel Kod {isEditing && <button type="button" className="quick-add-btn" onClick={()=>setShowQuickAdd('special-code-mgr')}>+</button>}</>}>
                  <select value={form.special_code_id||''} onChange={e=>f('special_code_id',e.target.value||null)} disabled={!isEditing}>
                    <option value="">Seciniz</option>
                    {(lookups.specialCodes||[]).map(s=><option key={s.id} value={s.id}>{s.code ? s.code + ' - ' : ''}{s.name}</option>)}
                  </select>
                </FormField>
                <FormField label="Satış Temsilcisi">
                  <select value={form.sales_rep_id||''} onChange={e=>f('sales_rep_id',e.target.value||null)} disabled={!isEditing}>
                    <option value="">Seciniz</option>
                    {lookups.reps.map(r=><option key={r.id} value={r.id}>{r.full_name || r.code}</option>)}
                  </select>
                </FormField>
                <FormField label="Bağlı Şube">
                  <select value={form.branch_id||''} onChange={e=>f('branch_id',e.target.value||null)} disabled={!isEditing}>
                    <option value="">Seciniz</option>
                    {(lookups.branches||[]).map(b=><option key={b.id} value={b.id}>{b.code ? b.code+' - ' : ''}{b.name}</option>)}
                  </select>
                </FormField>
                <FormField label="Durum">
                  <select value={form.is_active?'1':'0'} onChange={e=>f('is_active',e.target.value==='1')} disabled={!isEditing}>
                    <option value="1">Aktif</option>
                    <option value="0">Pasif</option>
                  </select>
                </FormField>
              </div>
            )}

            {activeTab === 'contact' && (
              <div style={gridStyle}>
                <FormField label="Telefon">
                  <input value={form.phone||''} onChange={e=>f('phone',e.target.value)} disabled={!isEditing}/>
                </FormField>
                <FormField label="Telefon 2">
                  <input value={form.phone2||''} onChange={e=>f('phone2',e.target.value)} disabled={!isEditing}/>
                </FormField>
                <FormField label="Cep Telefonu">
                  <input value={form.mobile||''} onChange={e=>f('mobile',e.target.value)} disabled={!isEditing}/>
                </FormField>
                <FormField label="E-posta">
                  <input value={form.email||''} onChange={e=>f('email',e.target.value)} disabled={!isEditing}/>
                </FormField>
                <FormField label="Web Sitesi">
                  <input value={form.website||''} onChange={e=>f('website',e.target.value)} disabled={!isEditing}/>
                </FormField>
                <FormField label="Ülke">
                  <select value={form.country||''} onChange={e=>{ f('country', e.target.value); f('city',''); f('district',''); }} disabled={!isEditing}>
                    <option value="">— Seçin —</option>
                    {countries.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                  </select>
                </FormField>
                <FormField label="Şehir" hint={!form.country ? 'Önce ülke seçin' : undefined}>
                  <select value={form.city||''} onChange={e=>{ f('city', e.target.value); f('district',''); }} disabled={!isEditing || !form.country}>
                    <option value="">— Seçin —</option>
                    {cities.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                  </select>
                </FormField>
                <FormField label="İlçe" hint={!form.city ? 'Önce şehir seçin' : undefined}>
                  <select value={form.district||''} onChange={e=>f('district', e.target.value)} disabled={!isEditing || !form.city}>
                    <option value="">— Seçin —</option>
                    {districts.map(d => <option key={d.id} value={d.name}>{d.name}</option>)}
                  </select>
                </FormField>
                <FormField label="Adres" fullWidth>
                  <textarea rows="3" value={form.address||''} onChange={e=>f('address',e.target.value)} disabled={!isEditing}/>
                </FormField>
              </div>
            )}

            {activeTab === 'financial' && (
              <div style={gridStyle}>
                <FormField label="Risk Limiti">
                  <input type="number" step="0.01" value={form.credit_limit||0} onChange={e=>f('credit_limit',parseFloat(e.target.value)||0)} disabled={!isEditing}/>
                </FormField>
                <FormField label="Limit Aşımı">
                  <select value={form.credit_limit_action||'warn'} onChange={e=>f('credit_limit_action',e.target.value)} disabled={!isEditing||!(form.credit_limit>0)}>
                    <option value="warn">Sadece bilgilendir</option>
                    <option value="confirm">Uyar - kullanıcıya sor</option>
                    <option value="block">Engelle (durdur)</option>
                  </select>
                </FormField>
                <FormField label="Vade (gün)">
                  <input type="number" value={form.payment_term_days||0} onChange={e=>f('payment_term_days',parseInt(e.target.value)||0)} disabled={!isEditing}/>
                </FormField>
                <FormField label="İskonto (%)">
                  <input type="number" step="0.01" value={form.discount_rate||0} onChange={e=>f('discount_rate',parseFloat(e.target.value)||0)} disabled={!isEditing}/>
                </FormField>
                <FormField label="Döviz">
                  <select value={form.currency_code||'TRY'} onChange={e=>f('currency_code',e.target.value)} disabled={!isEditing}>
                    {currencies.map(c=><option key={c.id||c.code} value={c.code}>{c.code} - {c.name}</option>)}
                  </select>
                </FormField>
                <FormField label="Bakiye">
                  <input value={fmtAmount(form.balance||0)} disabled style={{fontWeight:700,color:form.balance>0?'var(--danger)':form.balance<0?'var(--success)':'inherit'}}/>
                </FormField>
              </div>
            )}

            {activeTab === 'persons' && (
              <div>
                {!isNew && <div style={{marginBottom:12}}><Button size="sm" onClick={()=>setShowContactForm(true)}>+ Yetkili Ekle</Button></div>}
                {contacts.length === 0 ? <EmptyState title="Yetkili yok" description="Henüz yetkili eklenmemiş" compact /> :
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:'0.85rem'}}>
                  <thead><tr style={{borderBottom:'1px solid var(--border-secondary)'}}>
                    <th style={{textAlign:'left',padding:'8px',color:'var(--text-tertiary)',fontWeight:500}}>Ad Soyad</th>
                    <th style={{textAlign:'left',padding:'8px',color:'var(--text-tertiary)',fontWeight:500}}>Unvan</th>
                    <th style={{textAlign:'left',padding:'8px',color:'var(--text-tertiary)',fontWeight:500}}>Telefon</th>
                    <th style={{textAlign:'left',padding:'8px',color:'var(--text-tertiary)',fontWeight:500}}>E-posta</th>
                    <th style={{width:40}}></th>
                  </tr></thead>
                  <tbody>{contacts.map(c=>(
                    <tr key={c.id} style={{borderBottom:'1px solid var(--border-secondary)'}}>
                      <td style={{padding:'8px'}}>{c.full_name} {c.is_primary && <span style={{fontSize:'0.65rem',background:'var(--accent-glow)',color:'var(--accent-primary)',padding:'2px 6px',borderRadius:4,marginLeft:4}}>Birincil</span>}</td>
                      <td style={{padding:'8px',color:'var(--text-secondary)'}}>{c.title||'-'}</td>
                      <td style={{padding:'8px',color:'var(--text-secondary)'}}>{c.phone||c.mobile||'-'}</td>
                      <td style={{padding:'8px',color:'var(--text-secondary)'}}>{c.email||'-'}</td>
                      <td><Button variant="danger" size="sm" onClick={()=>handleDeleteContact(c.id)}>Sil</Button></td>
                    </tr>
                  ))}</tbody>
                </table>}
                {showContactForm && (
                  <div style={{marginTop:16,padding:16,background:'var(--bg-tertiary)',borderRadius:12}}>
                    <div style={gridStyle}>
                      <FormField label="Ad Soyad" required>
                        <input value={contactForm.full_name} onChange={e=>setContactForm(p=>({...p,full_name:e.target.value}))}/>
                      </FormField>
                      <FormField label="Unvan">
                        <input value={contactForm.title} onChange={e=>setContactForm(p=>({...p,title:e.target.value}))}/>
                      </FormField>
                      <FormField label="Telefon">
                        <input value={contactForm.phone} onChange={e=>setContactForm(p=>({...p,phone:e.target.value}))}/>
                      </FormField>
                      <FormField label="E-posta">
                        <input value={contactForm.email} onChange={e=>setContactForm(p=>({...p,email:e.target.value}))}/>
                      </FormField>
                    </div>
                    <div style={{marginTop:12,display:'flex',gap:8,justifyContent:'flex-end'}}>
                      <Button variant="ghost" onClick={()=>setShowContactForm(false)}>İptal</Button>
                      <Button size="sm" onClick={handleAddContact}>Ekle</Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'addresses' && (
              <div>
                {isNew ? (
                  <EmptyState title="Önce kaydet" description="Adres eklemek için cariyi önce kaydedin" compact />
                ) : (
                  <>
                    {!showAddrForm && (
                      <div style={{ marginBottom: 12 }}>
                        <Button size="sm" onClick={() => {
                          setEditingAddrId(null);
                          setAddrForm({ label: '', contact_person: '', phone: '', address: '', district: '', city: '', country: 'Türkiye', postal_code: '', is_default: false, notes: '' });
                          setShowAddrForm(true);
                        }}>+ Adres Ekle</Button>
                      </div>
                    )}
                    {addresses.length === 0 ? (
                      <EmptyState title="Adres yok" description="Henüz adres eklenmemiş" compact />
                    ) : (
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                        <thead>
                          <tr style={{ borderBottom: '1px solid var(--border-secondary)' }}>
                            <th style={{ textAlign: 'left', padding: '8px', color: 'var(--text-tertiary)', fontWeight: 500 }}>Etiket</th>
                            <th style={{ textAlign: 'left', padding: '8px', color: 'var(--text-tertiary)', fontWeight: 500 }}>Adres</th>
                            <th style={{ textAlign: 'left', padding: '8px', color: 'var(--text-tertiary)', fontWeight: 500 }}>Şehir / İlçe</th>
                            <th style={{ textAlign: 'left', padding: '8px', color: 'var(--text-tertiary)', fontWeight: 500 }}>İlgili Kişi</th>
                            <th style={{ textAlign: 'left', padding: '8px', color: 'var(--text-tertiary)', fontWeight: 500 }}>Telefon</th>
                            <th style={{ width: 200 }}></th>
                          </tr>
                        </thead>
                        <tbody>{addresses.map(a => (
                          <tr key={a.id} style={{ borderBottom: '1px solid var(--border-secondary)' }}>
                            <td style={{ padding: '8px' }}>
                              {a.label}
                              {a.is_default && <span style={{ fontSize: '0.65rem', background: 'rgba(16,185,129,0.15)', color: 'var(--success)', padding: '2px 6px', borderRadius: 4, marginLeft: 6, fontWeight: 700 }}>Varsayılan</span>}
                            </td>
                            <td style={{ padding: '8px', color: 'var(--text-secondary)', maxWidth: 240 }}>{a.address || '-'}</td>
                            <td style={{ padding: '8px', color: 'var(--text-secondary)' }}>{[a.city, a.district].filter(Boolean).join(' / ') || '-'}</td>
                            <td style={{ padding: '8px', color: 'var(--text-secondary)' }}>{a.contact_person || '-'}</td>
                            <td style={{ padding: '8px', color: 'var(--text-secondary)' }}>{a.phone || '-'}</td>
                            <td style={{ padding: '8px', textAlign: 'right' }}>
                              {!a.is_default && <Button size="sm" variant="ghost" onClick={() => makeAddressDefault(a.id)} title="Varsayılan yap">★</Button>}
                              <Button size="sm" variant="ghost" onClick={() => { editAddress(a); setShowAddrForm(true); }}>Düzenle</Button>
                              <Button size="sm" variant="danger" onClick={() => deleteAddress(a.id)}>Sil</Button>
                            </td>
                          </tr>
                        ))}</tbody>
                      </table>
                    )}
                    {showAddrForm && (
                      <div style={{ marginTop: 16, padding: 16, background: 'var(--bg-tertiary)', borderRadius: 12 }}>
                        <div style={{ marginBottom: 10, fontWeight: 600, fontSize: '0.92rem' }}>
                          {editingAddrId ? 'Adresi Düzenle' : 'Yeni Adres'}
                        </div>
                        <div style={gridStyle}>
                          <FormField label="Etiket" required>
                            <input value={addrForm.label} onChange={e => setAddrForm(p => ({ ...p, label: e.target.value }))} placeholder="Merkez, Şube 1, Depo..." />
                          </FormField>
                          <FormField label="İlgili Kişi">
                            <input value={addrForm.contact_person} onChange={e => setAddrForm(p => ({ ...p, contact_person: e.target.value }))} />
                          </FormField>
                          <FormField label="Telefon">
                            <input value={addrForm.phone} onChange={e => setAddrForm(p => ({ ...p, phone: e.target.value }))} />
                          </FormField>
                          <FormField label="Posta Kodu">
                            <input value={addrForm.postal_code} onChange={e => setAddrForm(p => ({ ...p, postal_code: e.target.value }))} />
                          </FormField>
                          <FormField label="Ülke">
                            <select value={addrForm.country || ''} onChange={e => setAddrForm(p => ({ ...p, country: e.target.value, city: '', district: '' }))}>
                              <option value="">— Seçin —</option>
                              {countries.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                            </select>
                          </FormField>
                          <FormField label="Şehir" hint={!addrForm.country ? 'Önce ülke seçin' : undefined}>
                            <select value={addrForm.city || ''} onChange={e => setAddrForm(p => ({ ...p, city: e.target.value, district: '' }))} disabled={!addrForm.country}>
                              <option value="">— Seçin —</option>
                              {addrCities.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                            </select>
                          </FormField>
                          <FormField label="İlçe" hint={!addrForm.city ? 'Önce şehir seçin' : undefined}>
                            <select value={addrForm.district || ''} onChange={e => setAddrForm(p => ({ ...p, district: e.target.value }))} disabled={!addrForm.city}>
                              <option value="">— Seçin —</option>
                              {addrDistricts.map(d => <option key={d.id} value={d.name}>{d.name}</option>)}
                            </select>
                          </FormField>
                          <FormField label="Adres" required fullWidth>
                            <textarea rows="2" value={addrForm.address} onChange={e => setAddrForm(p => ({ ...p, address: e.target.value }))} placeholder="Sokak, mahalle, bina no..." />
                          </FormField>
                          <FormField label="Notlar" fullWidth>
                            <input value={addrForm.notes} onChange={e => setAddrForm(p => ({ ...p, notes: e.target.value }))} />
                          </FormField>
                        </div>
                        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.84rem', marginTop: 10 }}>
                          <input type="checkbox" checked={!!addrForm.is_default} onChange={e => setAddrForm(p => ({ ...p, is_default: e.target.checked }))} />
                          Varsayılan adres olarak işaretle
                        </label>
                        <div style={{ marginTop: 12, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                          <Button variant="ghost" onClick={() => {
                            setShowAddrForm(false);
                            setEditingAddrId(null);
                            setAddrForm({ label: '', contact_person: '', phone: '', address: '', district: '', city: '', country: 'Türkiye', postal_code: '', is_default: false, notes: '' });
                          }}>İptal</Button>
                          <Button size="sm" onClick={async () => { await saveAddress(); setShowAddrForm(false); }}>{editingAddrId ? 'Güncelle' : 'Ekle'}</Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {activeTab === 'banks' && (
              <div>
                {!isNew && <div style={{marginBottom:12}}><Button size="sm" onClick={()=>setShowBankForm(true)}>+ Banka Ekle</Button></div>}
                {banks.length === 0 ? <EmptyState title="Banka yok" description="Henüz banka eklenmemiş" compact /> :
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:'0.85rem'}}>
                  <thead><tr style={{borderBottom:'1px solid var(--border-secondary)'}}>
                    <th style={{textAlign:'left',padding:'8px',color:'var(--text-tertiary)',fontWeight:500}}>Banka</th>
                    <th style={{textAlign:'left',padding:'8px',color:'var(--text-tertiary)',fontWeight:500}}>Şube</th>
                    <th style={{textAlign:'left',padding:'8px',color:'var(--text-tertiary)',fontWeight:500}}>IBAN</th>
                    <th style={{textAlign:'left',padding:'8px',color:'var(--text-tertiary)',fontWeight:500}}>Döviz</th>
                    <th style={{width:40}}></th>
                  </tr></thead>
                  <tbody>{banks.map(b=>(
                    <tr key={b.id} style={{borderBottom:'1px solid var(--border-secondary)'}}>
                      <td style={{padding:'8px'}}>{b.bank_name} {b.is_default && <span style={{fontSize:'0.65rem',background:'var(--accent-glow)',color:'var(--accent-primary)',padding:'2px 6px',borderRadius:4,marginLeft:4}}>Varsayılan</span>}</td>
                      <td style={{padding:'8px',color:'var(--text-secondary)'}}>{b.branch_name||'-'}</td>
                      <td style={{padding:'8px',fontFamily:'monospace',fontSize:'0.8rem'}}>{b.iban||'-'}</td>
                      <td style={{padding:'8px',color:'var(--text-secondary)'}}>{b.currency_code}</td>
                      <td><Button variant="danger" size="sm" onClick={()=>handleDeleteBank(b.id)}>Sil</Button></td>
                    </tr>
                  ))}</tbody>
                </table>}
                {showBankForm && (
                  <div style={{marginTop:16,padding:16,background:'var(--bg-tertiary)',borderRadius:12}}>
                    <div style={gridStyle}>
                      <FormField label="Banka Adı" required>
                        <input value={bankForm.bank_name} onChange={e=>setBankForm(p=>({...p,bank_name:e.target.value}))}/>
                      </FormField>
                      <FormField label="Şube">
                        <input value={bankForm.branch_name} onChange={e=>setBankForm(p=>({...p,branch_name:e.target.value}))}/>
                      </FormField>
                      <FormField label="IBAN">
                        <input value={bankForm.iban} onChange={e=>setBankForm(p=>({...p,iban:e.target.value}))}/>
                      </FormField>
                      <FormField label="Döviz">
                        <select value={bankForm.currency_code} onChange={e=>setBankForm(p=>({...p,currency_code:e.target.value}))}>
                          {currencies.map(c=><option key={c.id||c.code} value={c.code}>{c.code}</option>)}
                        </select>
                      </FormField>
                    </div>
                    <div style={{marginTop:12,display:'flex',gap:8,justifyContent:'flex-end'}}>
                      <Button variant="ghost" onClick={()=>setShowBankForm(false)}>İptal</Button>
                      <Button size="sm" onClick={handleAddBank}>Ekle</Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'transactions' && (
              <div>
                {transactions.length === 0 ? <EmptyState title="Hareket yok" description="Henüz hareket yok" compact /> :
                <div style={{overflowX:'auto'}}>
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:'0.8rem',minWidth:1200}}>
                  <thead>
                    <tr style={{borderBottom:'1px solid var(--border-secondary)'}}>
                      <th rowSpan="2" style={{width:32,verticalAlign:'bottom'}}></th>
                      <th rowSpan="2" style={{textAlign:'left',padding:'6px 8px',color:'var(--text-tertiary)',fontWeight:500,verticalAlign:'bottom'}}>Tarih</th>
                      <th rowSpan="2" style={{textAlign:'left',padding:'6px 8px',color:'var(--text-tertiary)',fontWeight:500,verticalAlign:'bottom'}}>Belge Tipi</th>
                      <th rowSpan="2" style={{textAlign:'left',padding:'6px 8px',color:'var(--text-tertiary)',fontWeight:500,verticalAlign:'bottom'}}>Belge No</th>
                      <th rowSpan="2" style={{textAlign:'left',padding:'6px 8px',color:'var(--text-tertiary)',fontWeight:500,verticalAlign:'bottom'}}>Açıklama</th>
                      <th colSpan="4" style={{textAlign:'center',padding:'4px 8px',color:'var(--accent-primary)',fontWeight:600,borderBottom:'1px solid var(--border-secondary)',borderLeft:'1px solid var(--border-secondary)',fontSize:'0.75rem'}}>YEREL PARA BIRIMI</th>
                      <th colSpan="5" style={{textAlign:'center',padding:'4px 8px',color:'var(--info)',fontWeight:600,borderBottom:'1px solid var(--border-secondary)',borderLeft:'1px solid var(--border-secondary)',fontSize:'0.75rem'}}>ISLEM DOVIZI</th>
                    </tr>
                    <tr style={{borderBottom:'2px solid var(--border-secondary)'}}>
                      <th style={{textAlign:'right',padding:'4px 8px',color:'var(--text-tertiary)',fontWeight:500,borderLeft:'1px solid var(--border-secondary)',fontSize:'0.75rem'}}>Borç</th>
                      <th style={{textAlign:'right',padding:'4px 8px',color:'var(--text-tertiary)',fontWeight:500,fontSize:'0.75rem'}}>Alacak</th>
                      <th style={{textAlign:'right',padding:'4px 8px',color:'var(--text-tertiary)',fontWeight:500,fontSize:'0.75rem'}}>Bakiye</th>
                      <th style={{textAlign:'center',padding:'4px 8px',color:'var(--text-tertiary)',fontWeight:500,fontSize:'0.75rem'}}>PB</th>
                      <th style={{textAlign:'right',padding:'4px 8px',color:'var(--text-tertiary)',fontWeight:500,borderLeft:'1px solid var(--border-secondary)',fontSize:'0.75rem'}}>Borç</th>
                      <th style={{textAlign:'right',padding:'4px 8px',color:'var(--text-tertiary)',fontWeight:500,fontSize:'0.75rem'}}>Alacak</th>
                      <th style={{textAlign:'right',padding:'4px 8px',color:'var(--text-tertiary)',fontWeight:500,fontSize:'0.75rem'}}>Bakiye</th>
                      <th style={{textAlign:'center',padding:'4px 8px',color:'var(--text-tertiary)',fontWeight:500,fontSize:'0.75rem'}}>PB</th>
                      <th style={{textAlign:'right',padding:'4px 8px',color:'var(--text-tertiary)',fontWeight:500,fontSize:'0.75rem'}}>Kur</th>
                    </tr>
                  </thead>
                  <tbody>{(() => {
                    let localRunning = 0;
                    let foreignRunning = 0;
                    return transactions.map(t => {
                      const localCur = 'TRY';
                      const isLocal = !t.foreign_currency || t.foreign_currency === t.currency_code;
                      const localDebit = t.debit_local || (isLocal ? t.debit : (t.debit||0) * (t.exchange_rate||1)) || 0;
                      const localCredit = t.credit_local || (isLocal ? t.credit : (t.credit||0) * (t.exchange_rate||1)) || 0;
                      localRunning += localDebit - localCredit;
                      const foreignDebit = t.foreign_debit || (isLocal ? 0 : (t.debit||0));
                      const foreignCredit = t.foreign_credit || (isLocal ? 0 : (t.credit||0));
                      const hasForeign = t.foreign_currency && t.foreign_currency !== (t.currency_code||"TRY"); if (hasForeign) foreignRunning += foreignDebit - foreignCredit;
                      return (
                      <tr key={t.id} style={{borderBottom:'1px solid var(--border-secondary)'}}>
                        <td style={{padding:'4px 6px',textAlign:'center'}}>
                          {(t.source_id || ['cari_opening','cari_debit','cari_credit','cari_virman_in','cari_virman_out'].includes(t.doc_type)) && (
                            <button onClick={() => openLinkedDoc(t)} title="Bağlı belgeyi aç"
                              style={{background:'transparent',border:'1px solid var(--border-secondary)',borderRadius:6,padding:'3px 6px',cursor:'pointer',color:'var(--accent-primary)',fontSize:'0.85rem'}}>
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
                            </button>
                          )}
                        </td>
                        <td style={{padding:'6px 8px',whiteSpace:'nowrap'}}>{new Date(t.date).toLocaleDateString('tr-TR')}</td>
                        <td style={{padding:'6px 8px',whiteSpace:'nowrap'}}>
                          {(() => {
                            const map = {
                              invoice: { label: 'Fatura', color: '#3b82f6' },
                              sales: { label: 'Satış Faturası', color: '#16a34a' },
                              purchase: { label: 'Alış Faturası', color: '#3b82f6' },
                              sales_return: { label: 'Satış İade Faturası', color: '#d97706' },
                              purchase_return: { label: 'Alış İade Faturası', color: '#d97706' },
                              retail_sale: { label: 'Perakende Satış Faturası', color: '#16a34a' },
                              retail_sale_return: { label: 'Perakende Satış İade Faturası', color: '#d97706' },
                              cari_opening: { label: 'Açılış Fişi', color: '#7c3aed' },
                              cari_debit: { label: 'Borç Dekontu', color: '#dc2626' },
                              cari_credit: { label: 'Alacak Dekontu', color: '#16a34a' },
                              cari_virman_in: { label: 'Virman (Gelen)', color: '#0284c7' },
                              cari_virman_out: { label: 'Virman (Giden)', color: '#d97706' },
                              payment: { label: 'Tediye', color: '#dc2626' },
                              collection: { label: 'Tahsilat', color: '#16a34a' },
                              check_in:   { label: 'Çek Girişi',    color: '#7c3aed' },
                              check_out:  { label: 'Çek Çıkışı',    color: '#dc2626' },
                              note_in:    { label: 'Senet Girişi',  color: '#7c3aed' },
                              note_out:   { label: 'Senet Çıkışı',  color: '#dc2626' },
                              expense_voucher: { label: 'Gider Fişi', color: '#dc2626' },
                            };
                            const m = map[t.doc_type];
                            return m ? (
                              <span style={{ background: m.color + '22', color: m.color, padding: '2px 8px', borderRadius: 4, fontSize: '0.72rem', fontWeight: 600 }}>
                                {m.label}
                              </span>
                            ) : (
                              <span style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)' }}>{t.doc_type || '-'}</span>
                            );
                          })()}
                        </td>
                        <td style={{padding:'6px 8px',color:'var(--text-secondary)',fontFamily:'monospace',fontSize:'0.75rem'}}>{t.doc_number||'-'}</td>
                        <td style={{padding:'6px 8px',minWidth:280,maxWidth:400,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}} title={t.description||''}>{t.description||'-'}</td>
                        <td style={{padding:'6px 8px',textAlign:'right',color:'var(--danger)',borderLeft:'1px solid var(--border-secondary)'}}>{localDebit>0?fmtAmount(localDebit):'-'}</td>
                        <td style={{padding:'6px 8px',textAlign:'right',color:'var(--success)'}}>{localCredit>0?fmtAmount(localCredit):'-'}</td>
                        <td style={{padding:'6px 8px',textAlign:'right',fontWeight:600,color:localRunning>=0?'var(--danger)':'var(--success)'}}>{fmtAmount(Math.abs(localRunning))}{localRunning>=0?' B':' A'}</td>
                        <td style={{padding:'6px 8px',textAlign:'center',fontSize:'0.72rem',color:'var(--text-tertiary)'}}>{localCur}</td>
                        <td style={{padding:'6px 8px',textAlign:'right',color:isLocal?'var(--text-tertiary)':foreignDebit>0?'var(--danger)':'inherit',borderLeft:'1px solid var(--border-secondary)'}}>{isLocal?'-':foreignDebit>0?fmtAmount(foreignDebit):'-'}</td>
                        <td style={{padding:'6px 8px',textAlign:'right',color:isLocal?'var(--text-tertiary)':foreignCredit>0?'var(--success)':'inherit'}}>{isLocal?'-':foreignCredit>0?fmtAmount(foreignCredit):'-'}</td>
                        <td style={{padding:'6px 8px',textAlign:'right',fontWeight:isLocal?400:600,color:isLocal?'var(--text-tertiary)':foreignRunning>=0?'var(--danger)':'var(--success)'}}>{isLocal?'-':fmtAmount(Math.abs(foreignRunning))+(foreignRunning>=0?' B':' A')}</td>
                        <td style={{padding:'6px 8px',textAlign:'center',fontSize:'0.72rem',color:isLocal?'var(--text-tertiary)':'var(--accent-primary)'}}>{isLocal?'-':(t.foreign_currency||t.currency_code)}</td>
                        <td style={{padding:'6px 8px',textAlign:'right',fontSize:'0.75rem',fontFamily:'monospace',color:isLocal?'var(--text-tertiary)':'var(--text-secondary)'}}>{isLocal?'-':fmtRate(t.exchange_rate||1)}</td>
                      </tr>);
                    });
                  })()}</tbody>
                </table>
                </div>}
              </div>
            )}

            {activeTab === 'waybills' && (
              <div style={{ padding: '12px 0' }}>
                <div style={{ marginBottom: 10, padding: '8px 12px', background: 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.2)', borderRadius: 8, fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
                  ℹ İrsaliyeler teslim belgesidir; <strong>cari bakiyeyi etkilemez</strong>. Faturalanmamış sevkiyatları takip için bu liste vardır.
                </div>
                {waybillsLoading ? (
                  <div style={{ textAlign: 'center', padding: 24, color: 'var(--text-tertiary)' }}>Yükleniyor...</div>
                ) : waybills.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: 32, color: 'var(--text-tertiary)', fontSize: '0.85rem' }}>Bu cariye ait irsaliye yok.</div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.83rem' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid var(--border-secondary)' }}>
                          <th style={{ padding: '6px 8px', textAlign: 'left', color: 'var(--text-tertiary)', fontWeight: 500 }}>Tarih</th>
                          <th style={{ padding: '6px 8px', textAlign: 'left', color: 'var(--text-tertiary)', fontWeight: 500 }}>Tip</th>
                          <th style={{ padding: '6px 8px', textAlign: 'left', color: 'var(--text-tertiary)', fontWeight: 500 }}>Belge No</th>
                          <th style={{ padding: '6px 8px', textAlign: 'left', color: 'var(--text-tertiary)', fontWeight: 500 }}>Depo</th>
                          <th style={{ padding: '6px 8px', textAlign: 'center', color: 'var(--text-tertiary)', fontWeight: 500 }}>Durum</th>
                          <th style={{ padding: '6px 8px', textAlign: 'right', color: 'var(--text-tertiary)', fontWeight: 500 }}>Tutar</th>
                          <th style={{ padding: '6px 8px', textAlign: 'center', color: 'var(--text-tertiary)', fontWeight: 500 }}>PB</th>
                        </tr>
                      </thead>
                      <tbody>
                        {waybills.map(w => {
                          const isReturn = (w.waybill_type || '').includes('return');
                          const isSales = w.direction === 'sales';
                          const typeLabel = isSales
                            ? (isReturn ? 'Satış İade İrsaliyesi' : 'Satış İrsaliyesi')
                            : (isReturn ? 'Alış İade İrsaliyesi' : 'Alış İrsaliyesi');
                          const typeColor = isSales ? '#16a34a' : '#3b82f6';
                          const statusMap = { draft: 'Taslak', approved: 'Onaylı', cancelled: 'İptal', invoiced: 'Faturalandı' };
                          const statusColor = w.status === 'approved' ? '#16a34a' : w.status === 'cancelled' ? '#64748b' : w.status === 'invoiced' ? '#7c3aed' : '#d97706';
                          return (
                            <tr key={w.id} style={{ borderBottom: '1px solid var(--border-secondary)' }}>
                              <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>{new Date(w.waybill_date).toLocaleDateString('tr-TR')}</td>
                              <td style={{ padding: '6px 8px' }}>
                                <span style={{ fontSize: '0.72rem', padding: '2px 8px', borderRadius: 4, fontWeight: 600, background: typeColor + '22', color: typeColor }}>{typeLabel}</span>
                              </td>
                              <td style={{ padding: '6px 8px', fontFamily: 'monospace', fontSize: '0.78rem', color: 'var(--text-secondary)' }}>{w.document_number || '-'}</td>
                              <td style={{ padding: '6px 8px', color: 'var(--accent-primary)' }}>{w.warehouse_name || '-'}</td>
                              <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                                <span style={{ fontSize: '0.7rem', padding: '2px 7px', borderRadius: 4, background: statusColor + '18', color: statusColor }}>{statusMap[w.status] || w.status}</span>
                              </td>
                              <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 600 }}>{fmtAmount(w.grand_total || 0)}</td>
                              <td style={{ padding: '6px 8px', textAlign: 'center', fontSize: '0.74rem', color: 'var(--text-tertiary)' }}>{w.currency_code || 'TRY'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'image' && (
              <div style={{padding:'16px 0'}}>
                {form.image_url ? (
                  <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:16}}>
                    <div className="img-preview-wrap">
                      <img src={IMG_BASE + form.image_url} alt="Cari resim" />
                      {!isNew && <button className="img-remove-btn" title="Resmi sil" onClick={handleImageDelete}>✕</button>}
                    </div>
                    {!isNew && <Button variant="ghost" size="sm" loading={imgUploading} onClick={() => imgInputRef.current?.click()}>Resmi Değiştir</Button>}
                  </div>
                ) : (
                  isNew ? (
                    <EmptyState title="Resim yüklenemiyor" description="Cari kartı kaydettikten sonra resim yükleyebilirsiniz." compact />
                  ) : (
                    <div className="img-upload-area" onClick={() => imgInputRef.current?.click()}>
                      <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                      <span>{imgUploading ? 'Yükleniyor...' : 'Logo veya resim yüklemek için tıklayın'}</span>
                      <small>JPG, PNG, WEBP — maks 5 MB</small>
                    </div>
                  )
                )}
                <input ref={imgInputRef} type="file" accept="image/*" style={{display:'none'}} onChange={handleImageUpload} />
              </div>
            )}

            {activeTab === 'scorecard' && selected?.id && (
              <VendorScorecard accountId={selected.id} months={12} />
            )}

            {activeTab === 'custom' && (
              selected?.id ? (
                <CustomFieldsSection
                  entityType="account"
                  entityId={selected.id}
                  autoSave
                  disabled={!isEditing}
                />
              ) : (
                <div style={{ padding: 20, color: 'var(--text-tertiary)', fontSize: '0.9rem' }}>
                  Özel alanları doldurmak için önce kaydı oluşturun.
                </div>
              )
            )}

            {activeTab === 'documents' && (
              <AttachmentManager entityType="account" entityId={selected?.id} />
            )}
            {activeTab === 'notes' && (
              <FormField label="Notlar" fullWidth>
                <textarea rows="6" value={form.notes||''} onChange={e=>f('notes',e.target.value)} disabled={!isEditing}/>
              </FormField>
            )}
          </Modal>
        );
      })()}

      {showQuickAdd === 'group-mgr' && (
        <QuickListManager open={true} title="Cari Grubu"
          items={lookups.groups || []} apiBase="/accounts/groups"
          onClose={() => setShowQuickAdd(null)} onChanged={fetchLookups} />
      )}
      {showQuickAdd === 'category-mgr' && (
        <QuickListManager open={true} title="Cari Kategorisi"
          items={lookups.categories || []} apiBase="/accounts/categories"
          onClose={() => setShowQuickAdd(null)} onChanged={fetchLookups} />
      )}
      {showQuickAdd === 'region-mgr' && (
        <QuickListManager open={true} title="Bölge"
          items={lookups.regions || []} apiBase="/accounts/regions"
          onClose={() => setShowQuickAdd(null)} onChanged={fetchLookups} />
      )}
      {showQuickAdd === 'special-code-mgr' && (
        <QuickListManager open={true} title="Cari Özel Kodu"
          items={lookups.specialCodes || []} apiBase="/accounts/special-codes"
          onClose={() => setShowQuickAdd(null)} onChanged={fetchLookups} />
      )}

    </div>
  );
}
export default AccountsPage;
